import { useState } from "react";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import "../DocumentBuilder/DocumentEditor/Editor.css";
import { documentBuilderService } from "../../services/documentBuilderService";

import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
} from "@mui/material";

import { AIChatBoxProps } from "./DocumentBuilderType";

export function InternalAIChatBox({
  selection,
  onReplace,
  onClose,
}: AIChatBoxProps) {
  const [prompt, setPrompt] = useState("");
  const [aiText, setAiText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!prompt) return;

    setLoading(true);

    try {
      const result = await documentBuilderService.editSelectedText(
        selection.text,
        prompt,
      );

      setAiText(result);
    } catch (error) {
      console.error("AI Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      className="ai-chat-box "
      elevation={8}
      sx={{
        width: 360,
        maxWidth: "90vw",
        backgroundColor: (theme) => theme.palette.background.paper,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        {/* ───── HEADER ───── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoFixHighIcon fontSize="small" />
            <Typography fontWeight={600}>AI Assistant</Typography>
          </Stack>

          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 1 }} />

        {/* ───── SELECTED TEXT ───── */}
        <Box mb={1}>
          <Typography variant="caption" color="text.secondary">
            Selected
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              color: (theme) => theme.palette.text.primary,
            }}
          >
            {selection.text}
          </Typography>
        </Box>

        {/* ───── PROMPT INPUT ───── */}
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="Tell AI what to do…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{
            "& .MuiInputBase-input": {
              color: (theme) => theme.palette.text.secondary,
            },
          }}
        />

        {/* ───── AI RESULT ───── */}
        {aiText && (
          <Box
            mt={2}
            p={1.5}
            borderRadius={1}
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.grey[50],
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              AI Result
            </Typography>

            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
            >
              {aiText}
            </Typography>
          </Box>
        )}

        {/* ───── ACTIONS ───── */}
        <Stack direction="row" spacing={1} justifyContent="flex-start" mt={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? "Generating…" : "Generate"}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<SendIcon />}
            onClick={() => onReplace(aiText)}
            disabled={!aiText}
            sx={{
              color: "#fff",
              "& .MuiButton-startIcon": {
                color: "#fff",
              },
              "&.Mui-disabled": {
                color: "#fff",
                "& .MuiButton-startIcon": {
                  color: "#fff",
                },
              },
            }}
          >
            Update
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
